import { useSuspenseQuery } from '@apollo/client/react';
import { useState } from 'react';

import { QueryOrderEnum } from 'src/graphql/schema';
import { LegacyReposDocument } from 'src/graphql/actions/legacyRepos/legacyRepos.query';
import { withSuspense } from 'src/hoc';
import { useLocalStorage } from 'src/hooks';
import { LEGACY_REPOS_LIMIT } from 'src/shared/constants';
import { createRequiredContext } from 'src/context/createRequiredContext';

import type { ILegacyRepo } from 'src/graphql/schema';
import type { ILegacyReposQueryVariables } from 'src/graphql/actions/legacyRepos/legacyRepos.query';

import { useMigrationsUiSlice } from './migrationsSlice';

const [useMigrations, MigrationsProvider] = createRequiredContext<{
  legacy: {
    count: number;
    repos: ILegacyRepo[];
    variables?: ILegacyReposQueryVariables;
    pagesCount: number;
  };
  totalLegacyReposCount: number;
  enableBulk: boolean;
  refetch: () => void;
  ui: {
    isInfoModalVisible: boolean;
  } & ReturnType<typeof useMigrationsUiSlice>;
  setIsInfoModalVisible: (isVisible: boolean) => void;
  onLegacyVariablesChange: (variables: Partial<ILegacyReposQueryVariables>) => void;
}>();

const MigrationsContextProvider = withSuspense(({ children }: { children: React.ReactNode }) => {
  const [legacyVariables, setLegacyVariables] = useState<ILegacyReposQueryVariables>({
    perPage: LEGACY_REPOS_LIMIT,
    order: QueryOrderEnum.Desc,
  });
  const { data: totalCount } = useSuspenseQuery(LegacyReposDocument, {
    variables: {
      perPage: 1,
    },
  });
  const { data, refetch } = useSuspenseQuery(LegacyReposDocument, {
    variables: legacyVariables,
  });
  const migrationsUi = useMigrationsUiSlice();
  const [isInfoModalVisible, setIsInfoModalVisible] = useLocalStorage('migrations-info-modal-displayed', true);

  const onLegacyVariablesChange = (variables: Partial<ILegacyReposQueryVariables>) => {
    setLegacyVariables((prev) => ({ ...prev, ...variables }));
  };

  const enableBulk = new Set(data.legacyRepos.nodes.map(({ platform }) => platform)).size <= 1;

  const providerValue = {
    legacy: {
      count: data.legacyRepos.nodesCount,
      repos: data.legacyRepos.nodes as ILegacyRepo[],
      variables: legacyVariables,
      pagesCount: data.legacyRepos.pagesCount,
    },
    enableBulk,
    refetch,
    ui: {
      isInfoModalVisible,
      ...migrationsUi,
    },
    setIsInfoModalVisible,
    onLegacyVariablesChange,
    totalLegacyReposCount: totalCount.legacyRepos.nodesCount,
  };

  return <MigrationsProvider value={providerValue}>{children}</MigrationsProvider>;
});

export { useMigrations, MigrationsContextProvider };
